// urlConfig.js
import DEFAULT_IMAGE_URL from './assets/noPhoto1.png';

const env = process.env || {};

const trimEndSlash = (value = '') => String(value || '').trim().replace(/\/+$/, '');

const normalizePrefix = (value = '') => {
    const source = String(value || '').trim();

    if (!source) return '';

    return `/${source.replace(/^\/+|\/+$/g, '')}`;
};

const joinUrl = (origin, ...parts) => {
    const base = trimEndSlash(origin);

    const path = parts
        .filter(Boolean)
        .map((part) => String(part).replace(/^\/+|\/+$/g, ''))
        .filter(Boolean)
        .join('/');

    return path ? `${base}/${path}` : base;
};

const API_ORIGIN = trimEndSlash(
    env.REACT_APP_API_ORIGIN ||
    env.REACT_APP_BASE_URL ||
    'https://admin-stlkirovsk51.ru'
);

const API_PREFIX = normalizePrefix(env.REACT_APP_API_PREFIX || '/api-v3/api');
const PUBLIC_PREFIX = normalizePrefix(env.REACT_APP_PUBLIC_PREFIX || '/public-v3');
const MEDIA_PREFIX = normalizePrefix(env.REACT_APP_MEDIA_PREFIX || '/media-v3');
const SOCKET_PATH = normalizePrefix(env.REACT_APP_SOCKET_PATH || '/socket.io');

const getWindowOrigin = () => {
    if (typeof window === 'undefined') return '';

    return trimEndSlash(window.location?.origin || '');
};

const isLocalOrigin = (origin = '') => {
    try {
        const url = new URL(origin);

        return ['localhost', '127.0.0.1', '0.0.0.0'].includes(url.hostname);
    } catch (_error) {
        return false;
    }
};

const runtimeOrigin = getWindowOrigin();
const configuredSiteOrigin = trimEndSlash(
    env.REACT_APP_SITE_ORIGIN ||
    env.REACT_APP_PUBLIC_SITE_ORIGIN ||
    env.REACT_APP_CLIENT_ORIGIN ||
    env.REACT_APP_FRONTEND_ORIGIN ||
    ''
);

export const siteOrigin = configuredSiteOrigin ||
    (runtimeOrigin && !isLocalOrigin(runtimeOrigin) ? runtimeOrigin : 'https://alotb.ru');

export const buildPublicSiteUrl = (path = '') => {
    const source = String(path || '').trim();

    if (!source) return siteOrigin;
    if (/^https?:\/\//i.test(source)) return source;

    return `${siteOrigin}${source.startsWith('/') ? '' : '/'}${source}`;
};

export const api = joinUrl(API_ORIGIN, API_PREFIX);

export const wsApi = trimEndSlash(
    env.REACT_APP_WS ||
    API_ORIGIN
);

export const socketPath = SOCKET_PATH;

let __imgFallbackInstalled = false;

function ensureImgFallbackOnce() {
    if (__imgFallbackInstalled || typeof window === 'undefined') return;

    __imgFallbackInstalled = true;

    window.addEventListener(
        'error',
        (e) => {
            const img = e?.target;

            if (img && img.tagName === 'IMG' && img.src !== DEFAULT_IMAGE_URL) {
                img.src = DEFAULT_IMAGE_URL;
            }
        },
        true
    );
}

const isFullUrl = (value) => /^https?:\/\//i.test(value);

const normalizeLegacyMediaPath = (source = '') => {
    const value = String(source || '').trim();

    if (!value) return '';

    if (value.startsWith(`${MEDIA_PREFIX}/`)) {
        return value;
    }

    if (value.startsWith(`${PUBLIC_PREFIX}/`)) {
        return value;
    }

    if (value.startsWith('/media/images/')) {
        return joinUrl(MEDIA_PREFIX, 'images', value.slice('/media/images/'.length));
    }

    if (value.startsWith('/media/chatImages/')) {
        return joinUrl(MEDIA_PREFIX, 'images', value.slice('/media/chatImages/'.length));
    }

    if (value.startsWith('/media/videos/')) {
        return joinUrl(MEDIA_PREFIX, 'videos', value.slice('/media/videos/'.length));
    }

    if (value.startsWith('/media/chatVideos/')) {
        return joinUrl(MEDIA_PREFIX, 'videos', value.slice('/media/chatVideos/'.length));
    }

    if (value.startsWith('/media/docs/')) {
        return joinUrl(MEDIA_PREFIX, 'docs', value.slice('/media/docs/'.length));
    }

    if (value.startsWith('/media/chatDocs/')) {
        return joinUrl(MEDIA_PREFIX, 'docs', value.slice('/media/chatDocs/'.length));
    }

    if (value.startsWith('/media/voice/')) {
        return joinUrl(MEDIA_PREFIX, 'voice', value.slice('/media/voice/'.length));
    }

    if (value.startsWith('/media/voiceMessages/')) {
        return joinUrl(MEDIA_PREFIX, 'voice', value.slice('/media/voiceMessages/'.length));
    }

    if (value.startsWith('/media/')) {
        return joinUrl(MEDIA_PREFIX, value.slice('/media/'.length));
    }

    if (value.startsWith('/public/')) {
        return joinUrl(PUBLIC_PREFIX, value.slice('/public/'.length));
    }

    if (value.startsWith('/uploads/')) {
        return joinUrl(PUBLIC_PREFIX, value.slice('/uploads/'.length));
    }

    if (value.startsWith('/uploadsNew/')) {
        return joinUrl(PUBLIC_PREFIX, value.slice('/uploadsNew/'.length));
    }

    if (value.startsWith('/chatImages/')) {
        return joinUrl(MEDIA_PREFIX, 'images', value.slice('/chatImages/'.length));
    }

    if (value.startsWith('/chatVideos/')) {
        return joinUrl(MEDIA_PREFIX, 'videos', value.slice('/chatVideos/'.length));
    }

    if (value.startsWith('/chatDocs/')) {
        return joinUrl(MEDIA_PREFIX, 'docs', value.slice('/chatDocs/'.length));
    }

    if (value.startsWith('/voiceMessages/')) {
        return joinUrl(MEDIA_PREFIX, 'voice', value.slice('/voiceMessages/'.length));
    }

    return value;
};

function resolveStaticUrl(path = '', defaultPrefix = PUBLIC_PREFIX) {
    ensureImgFallbackOnce();

    if (!path) return DEFAULT_IMAGE_URL;

    const source = String(path).trim();

    if (!source) return DEFAULT_IMAGE_URL;

    if (isFullUrl(source)) {
        return source;
    }

    const normalized = normalizeLegacyMediaPath(source);

    if (normalized.startsWith(`${MEDIA_PREFIX}/`) || normalized.startsWith(`${PUBLIC_PREFIX}/`)) {
        return joinUrl(API_ORIGIN, normalized);
    }

    return joinUrl(API_ORIGIN, defaultPrefix, normalized);
}

export function resolveAnyStaticUrl(path = '') {
    return resolveStaticUrl(path, PUBLIC_PREFIX);
}

export function generatePublicUrl(filename) {
    return resolveStaticUrl(filename, PUBLIC_PREFIX);
}

export function generateMediaUrl(filename) {
    return resolveStaticUrl(filename, MEDIA_PREFIX);
}

export const urlConfig = {
    api,
    wsApi,
    socketPath,
    siteOrigin,
    buildPublicSiteUrl,
    apiOrigin: API_ORIGIN,
    apiPrefix: API_PREFIX,
    publicPrefix: PUBLIC_PREFIX,
    mediaPrefix: MEDIA_PREFIX,
};

// const DEFAULT_IMAGE_URL = 'http://localhost:2001/public/default-image.jpg';
// import DEFAULT_IMAGE_URL from '../src/assets/noPhoto1.png'

// export const api = 'https://admin-mafkirovsk51.online/api';

// export const generatePublicUrl = (fileName) => {
//     if (!fileName) {
//         return DEFAULT_IMAGE_URL;
//     }
//     return `https://admin-mafkirovsk51.online/public/${fileName}`;
// }

// export const api = 'http://localhost:2001/api';
// export const generatePublicUrl = (filename) => {
//     if (!filename) {
//         return DEFAULT_IMAGE_URL;
//     }
//     return `http://localhost:2001/public/${filename}`;
// };


// export const api = 'https://192.168.1.137:2001/api'

// export const generatePublicUrl = (filename) => {
//     if (!filename) return DEFAULT_IMAGE_URL
//     return `https://192.168.1.137:2001/public/${filename}`
// }

// export const api = 'https://admin-stlkirovsk51.ru/api';
// export const generatePublicUrl = (fileName) => {
//     if (!fileName) {
//         return DEFAULT_IMAGE_URL;
//     }
//     return `https://admin-stlkirovsk51.ru/public/${fileName}`;
// }
